import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {Alert, Card, IconButton} from "@mui/material";
import {GET, PATCH, DELETE} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {useLocation} from "react-router-dom";
import {formatDate} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

export default function EmployeeDependents() {
  const [data, setData] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const location = useLocation();
  const empId = window.sessionStorage.getItem("empId") || location.state;
  const fetchUrl = `employee/employee-details/${empId}`;
  const updateUrl = `employee/dependent`;
  const [open, setOpen] = React.useState(false);
  const [newId, setNewId] = React.useState(1);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [snackbar, setSnackbar] = React.useState(null);

  const [hasDependentDetailsPermission, setHasDependentDetailsPermission] = React.useState(false);

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(fetchUrl);
        const data = response?.data?.data;
        setData(data?.dependents || []);
        setHasDependentDetailsPermission(data?.employee.permissions.includes("dependents"));
        const maxId =
          data?.dependents?.length > 0
            ? Math.max(...data.dependents.map(row => row.dependentId))
            : 0;
        setNewId(maxId + 1);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [fetchUrl]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = id => () => {
    if (!hasDependentDetailsPermission) return;
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = id => () => {
    if (!hasDependentDetailsPermission) return;
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleDeleteClick = id => async () => {
    if (!hasDependentDetailsPermission) return;
    try {
      const deleteUrl = `employee/delete-dependent/${id}`;
      let DeleteRes = await DELETE(deleteUrl);
      setData(data.filter(row => row.dependentId !== id));
      if (DeleteRes?.status === 200) {
        setSnakColor("success");
        setSnakMessage("Dependent deleted successfully");
        setOpen(true);

        let response = await GET(fetchUrl);
        const data = response?.data?.data;
        setData(data?.dependents || []);
        const maxId =
          data?.dependents?.length > 0
            ? Math.max(...data.dependents.map(row => row.dependentId))
            : 0;
        setNewId(maxId + 1);
      } else if (DeleteRes.statusCode == 409) {
        setSnakColor("error");
        setSnakMessage("Deletion failed");
        setOpen(true);
      }
    } catch (error) {
      console.error("Error deleting row:", error);
    }
  };

  const handleCancelClick = id => () => {
    if (!hasDependentDetailsPermission) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true}
    });

    const editedRow = data.find(row => row.dependentId === id);
    if (editedRow.isNew) {
      setData(data.filter(row => row.dependentId !== id));
      const maxId =
        data.length > 1
          ? Math.max(...data.filter(row => row.dependentId !== id).map(row => row.dependentId))
          : 0;
      setNewId(maxId + 1);
    }
  };

  const processRowUpdate = newRow => {
    if (!hasDependentDetailsPermission) return newRow;
    const updatedRow = {
      ...newRow,
      isNew: false,
      birthDate: newRow.birthDate ? dayjs(newRow.birthDate).format("YYYY-MM-DD") : null
    };

    setData(data =>
      data.map(row => (row.dependentId === updatedRow.dependentId ? updatedRow : row))
    );

    try {
      PATCH(updateUrl, {empId: empId, dependents: [updatedRow]})
        .then(response => {
          response = response.data;
          if (response?.statusCode === 200) {
            console.log("Updated successfully..");

            setSnakColor("success");
            setSnakMessage("Dependent updated successfully");
            setOpen(true);

            GET(fetchUrl).then(responseDt => {
              const data = responseDt?.data?.data;
              setData(data?.dependents || []);
              const maxId =
                data?.dependents?.length > 0
                  ? Math.max(...data.dependents.map(row => row.dependentId))
                  : 0;
              setNewId(maxId + 1);
            });
          }
        })
        .catch(error => {
          const err = error?.response;
          if (err.data?.statusCode == 400 || err.data?.statusCode === 409) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          } else if (err.data?.statusCode == 406) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          } else if (err.data?.statusCode == 403) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          }
        });
    } catch (error) {
      console.error("Error updating row:", error);
    }
    return updatedRow;
  };

  const handleProcessRowUpdateError = React.useCallback(error => {
    setSnackbar({children: error.message, severity: "error"});
  }, []);

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    {field: "dependentId", headerName: "ID", width: 80, hide: true},
    {
      field: "dependentName",
      headerName: "Dependent Name",
      width: 180,
      renderCell: params => params.value || "N/A",
      editable: hasDependentDetailsPermission
    },
    {
      field: "dependentRelation",
      headerName: "Relation",
      width: 150,
      editable: hasDependentDetailsPermission,
      type: "singleSelect",
      renderCell: params => (params.value !== null ? params.value : "Unknown"),
      valueOptions: ["Father", "Mother", "Son", "Daughter", "Spouse", "Brother", "Sister"]
    },
    {
      field: "birthDate",
      headerName: "Birth Date",
      width: 200,
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={params.value ? dayjs(params.value) : null}
            onChange={date =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: date ? date.format("YYYY-MM-DD") : null
              })
            }
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
                size: "medium",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    padding: "4px 8px",
                    height: "50px",
                    fontSize: "14px"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }
              }
            }}
            disabled={!hasDependentDetailsPermission}
          />
        </LocalizationProvider>
      ),
      editable: hasDependentDetailsPermission
    },
    {
      field: "permanentAddress",
      headerName: "Address",
      width: 200,
      align: "left",
      headerAlign: "left",
      renderCell: params => (params.value !== null ? params.value : "Unknown"),
      editable: hasDependentDetailsPermission
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{color: "primary.main"}}
              onClick={handleSaveClick(id)}
              disabled={!hasDependentDetailsPermission}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
              disabled={!hasDependentDetailsPermission}
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
            color="inherit"
            disabled={!hasDependentDetailsPermission}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
            disabled={!hasDependentDetailsPermission}
          />
        ];
      }
    }
  ];

  const handleNullValues = data => {
    return data && data.length
      ? data.map(row => ({
          ...row,
          dependentName: row.dependentName ?? "N/A",
          birthDate: row.birthDate ?? "Unknown",
          dependentRelation: row.dependentRelation ?? "N/A",
          permanentAddress: row.permanentAddress ?? "N/A"
        }))
      : [];
  };

  const cleanedRows = handleNullValues(data);

  return (
    <Card>
      <MDBox
        sx={{
          height: 300,
          width: "100%",
          "& .actions": {
            color: "text.secondary"
          },
          "& .textPrimary": {
            color: "text.primary"
          }
        }}
      >
        <DataGrid
          rows={cleanedRows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          getRowId={row => row.dependentId}
          columnVisibilityModel={{
            dependentId: false
          }}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          slots={{
            toolbar: EditToolbar
          }}
          slotProps={{
            toolbar: {
              data,
              setData,
              setRowModesModel,
              newId,
              setNewId,
              hasDependentDetailsPermission
            }
          }}
          initialState={{
            ...data,
            sorting: {
              sortModel: [{field: "dependentId", sort: "asc"}]
            }
          }}
          sx={{
            fontFamily: properties.fontFamily,
            fontSize: "14px",
            "& .MuiDataGrid-editInputCell": {
              border: "1px solid #e0e0e0",
              padding: "4px",
              backgroundColor: "#fff",
              borderRadius: "0px",
              "&:focus-within": {
                borderColor: "#e0e0e0",
                outline: "none",
                boxShadow: "none"
              }
            },
            "& .MuiInputBase-root": {
              borderRadius: "0px !important",
              boxShadow: "none !important"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #e0e0e0 !important"
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0"
            }
          }}
        />
        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </Card>
  );

  function EditToolbar(props) {
    const {data, setData, setRowModesModel, newId, setNewId, hasDependentDetailsPermission} = props;

    const handleClick = () => {
      if (!hasDependentDetailsPermission) return;
      const newRow = {
        dependentId: newId,
        dependentName: "",
        birthDate: null,
        dependentRelation: "",
        permanentAddress: "",
        isNew: true
      };
      setData(prevRows => [...(prevRows ? prevRows : []), newRow]);
      setRowModesModel(oldModel => ({
        ...oldModel,
        [newId]: {mode: GridRowModes.Edit, fieldToFocus: "dependentName"}
      }));
      setNewId(prevId => prevId + 1);
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleClick}
          disabled={!hasDependentDetailsPermission}
        >
          Add record
        </Button>
      </GridToolbarContainer>
    );
  }
}
